import { FACET_NULL } from "@ckgo2/shared/types";
import type { FacetBucket } from "@/components/common/FacetPanel";

/**
 * Facety pro seznamy, které chodí z API CELÉ (katalog: zájezdy, inspirace, destinace).
 *
 * Provozní seznamy (poptávky, faktury, dodavatelé) počítají kbelíky na serveru a stránkují
 * v SQL — tam se nedá jinak, jsou velké. Katalog admin načítá celý (destinace a inspirace
 * ve štíhlé projekci, stovky řádků; destinací je přes 870), takže tady stačí spočítat
 * totéž nad polem v paměti. Díky tomu vypadají VŠECHNY seznamy adminu stejně
 * (`FacetedListLayout` + `FacetPanel`, stav ve URL) bez nové serverové koncovky.
 *
 * Hledání tu není: filtr i řazení podle shody dělá `sortBySearchMatch` z `@ckgo2/shared`
 * (přesná shoda názvu, začíná na, obsahuje, jiné pole; pak země před lokalitou a teprve
 * pak zvolené řazení seznamu). Prostý filtr „obsahuje" by „Bali" nechal mezi lokalitami,
 * které Bali jen zmiňují.
 */

/**
 * Hodnota facety jedné položky: jedna hodnota, nebo seznam (vícehodnotová faseta, např. typy
 * inspirace). Prázdná hodnota (`null`, `undefined`, `""`, prázdný seznam) je kbelík `null`.
 */
export type FacetValue = string | readonly string[] | null | undefined;

/** Neprázdné hodnoty položky bez duplicit; prázdný výsledek = položka patří do kbelíku `null`. */
function facetKeys(raw: FacetValue): string[] {
  if (raw === undefined || raw === null) return [];
  if (typeof raw === "string") return raw === "" ? [] : [raw];
  return [...new Set(raw.filter((v) => v !== ""))];
}

/**
 * Kbelíky facety včetně prázdné hodnoty (`null` → `FACET_NULL` v UI). Položka se seznamem
 * hodnot přidá +1 pod každou z nich.
 */
export function countBuckets<T>(items: T[], value: (item: T) => FacetValue): FacetBucket[] {
  const counts = new Map<string | null, number>();
  for (const item of items) {
    const keys = facetKeys(value(item));
    for (const key of keys.length > 0 ? keys : [null]) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return [...counts.entries()].map(([value, count]) => ({ value, count }));
}

/**
 * Popisek hodnoty facety složený z řádků samotných (např. `destinationId` → `destinationName`),
 * takže stránka nemusí stahovat druhý seznam jen kvůli jménům. Neznámá hodnota se vrací
 * beze změny.
 */
export function labelLookup<T>(
  items: T[],
  value: (item: T) => string | null | undefined,
  label: (item: T) => string | null | undefined,
): (value: string) => string {
  const labels = new Map<string, string>();
  for (const item of items) {
    const v = value(item);
    const l = label(item);
    if (v && l) labels.set(v, l);
  }
  return (v) => labels.get(v) ?? v;
}

/**
 * Vyhovuje položka vybraným hodnotám? Prázdný výběr = facet neomezuje; prázdná hodnota projde
 * jen s `FACET_NULL` ve výběru; u seznamu hodnot stačí jedna vybraná.
 */
export function matchesFacet(selected: readonly string[], value: FacetValue): boolean {
  if (selected.length === 0) return true;
  const keys = facetKeys(value);
  if (keys.length === 0) return selected.includes(FACET_NULL);
  return keys.some((key) => selected.includes(key));
}

/** Řez stránky pro seznam, který je celý v paměti. */
export function pageSlice<T>(items: T[], page: number, limit: number): T[] {
  const start = Math.max(0, (page - 1) * limit);
  return items.slice(start, start + limit);
}

/**
 * Porovnání textů po česku (ch, háčky, čárky; velikost písmen nerozhoduje). Jediná
 * implementace je sdílená (`@ckgo2/shared`, `czechSort.ts`); tady jen re-export pro stránky,
 * které už importují z `clientFacets`.
 */
export { compareCzech } from "@ckgo2/shared/utils";

/** Novější první; chybějící datum jde na konec. Sloupce chodí jako `Date` i jako ISO řetězec. */
export function compareNewest(
  a: string | Date | null | undefined,
  b: string | Date | null | undefined,
): number {
  const ta = a ? new Date(a).getTime() : 0;
  const tb = b ? new Date(b).getTime() : 0;
  if (!ta && !tb) return 0;
  if (!ta) return 1;
  if (!tb) return -1;
  return tb - ta;
}
